/** The schedule module's API types: what its endpoints answer and take. */
import type { EmploymentType, RecordedBy, WorkerRole, WorkerTier } from '../core/index.js'

export type BidWindowStatus = 'upcoming' | 'open' | 'closed'

export interface WorkerBidWindow {
  workerId: string
  tier: WorkerTier
  status: BidWindowStatus
  opensAt: string
  closesAt: string
}

/** A day the worker would cover a Clinical Bridge gap, and who put it on their list. */
export interface ClinicalBridgePreBidDate {
  date: string
  extraCredit: number
  /** Null for a date an import loaded. */
  enrolledAt: string | null
  /** The worker from their app, their scheduler (a partner) acting for them, or the facility's import. */
  enrolledBy: 'worker' | 'partner' | 'import'
}

/** The facility's Clinical Bridge rules as the worker's record carries them; a facility with no configuration reads as switched off. */
export interface ClinicalBridgeRules {
  enabled: boolean
  /** 0 means a callout stays open until the shift starts. */
  responseWindowMinutes: number
  arrivalWindowMinutes: number
  scoreBoostPerShift: number
  payPremiumEnabled: boolean
  /** Per hour; null while the premium is off or unset. */
  payPremiumAmount: number | null
  /** Who may enroll: staff's say per worker, or by employment type under the facility's overtime policy. Absent from an API deployed before the scheduling rules. */
  eligibilityMode?: 'staff' | 'by_employment_type'
  /** A withdrawal closer than this to the day's first shift is recorded as late. */
  withdrawCutoffHours?: number
  /** The most days this worker may enroll for in one period; null when uncapped. */
  maxDaysPerPeriod?: number | null
}

/** A day withdrawn from inside the facility's cutoff, kept for the scoring engine to read later. */
export interface BridgeLateWithdrawal {
  date: string
  withdrawnAt: string
  hoursBefore: number
}

export interface ClinicalBridgeStatus {
  workerId: string
  /** Staff's say on whether the worker may enroll; every worker is ineligible until set. */
  eligible: boolean
  /** An older per-worker import field; `rules.responseWindowMinutes` is what applies. */
  responseWindowMinutes: number
  /** An older per-worker import field; `rules.scoreBoostPerShift` is what applies. */
  extraCreditPerShift: number
  preBidDates: ClinicalBridgePreBidDate[]
  /** Absent from an API deployed before the cutoff rule. */
  lateWithdrawals?: BridgeLateWithdrawal[]
  rules: ClinicalBridgeRules
}

/** One shift to publish, as `POST /facilities/{facilityId}/schedule/shifts` takes it; the API opens a seat per unit of capacity. */
export interface PublishShiftInput {
  date: string
  period: ShiftTypeKey
  startTime: string
  endTime: string
  unitId: string
  /** The role the shift is for; left out, any role. */
  role?: WorkerRole
  /** How many seats; left out, one. */
  capacity?: number
}

export interface AvailableShift {
  id: string
  date: string
  period: string
  startTime: string
  endTime: string
  unitId: string
  /** The role the shift is for, where the scheduler says; absent means any. */
  role?: WorkerRole
  /** Which seat of a shift published with a capacity above one this is; absent means the only or first seat (phase D). */
  seat?: number
  /** Credential codes the shift requires beyond the role's (phase D). */
  requiredCredentials?: string[]
}

// Shift preferences (the Shift Bidding Demo's wizard, as autonoME holds its answers; built 16 October 2026).

export type ShiftTypeKey = 'day' | 'evening' | 'night'
export type DayOffPriority = 'high' | 'medium' | 'low'

export interface ShiftTypePreference {
  shiftType: ShiftTypeKey
  /** False marks a shift type the worker will never do. */
  willDo: boolean
}

export interface DayOffPreference {
  date: string
  priority: DayOffPriority
}

/** A worker's preferences for one month: the worker alone writes it, staff and the scheduler read it. */
export interface ShiftPreferences {
  workerId: string
  facilityId: string
  /** `YYYY-MM`. */
  period: string
  /** Every shift type once, in preference order. */
  shiftTypes: ShiftTypePreference[]
  hours: { min: number; max: number }
  ptoDates: string[]
  daysOff: DayOffPreference[]
  /** The days the worker can work, for an employment type that marks availability rather than days off (part time and PRN by default); null or absent for everyone else. */
  availableDates?: string[] | null
  note: string | null
  submittedAt: string
  updatedAt: string
  updatedBy: RecordedBy
  version: number
}

/** A shift type the facility runs, at its catalogue's hours, for the form to offer. */
export interface OfferedShiftType {
  shiftType: ShiftTypeKey
  /** The catalogue's hours, as the facility's scheduling rules set them. */
  startTime: string
  endTime: string
  published: number
  /** The times this type's published shifts most often run at, where they differ from the catalogue's; null otherwise (absent from an API before 29 October 2026). */
  publishedTimes?: { startTime: string; endTime: string } | null
}

/** The worker's PTO as the form shows it: the balance HR holds (null when not recorded), the days asked for this month, and what is left. */
export interface PtoSummary {
  balanceDays: number | null
  requestedDays: number
  remainingDays: number | null
}

/** A month the worker gave preferences for that no longer takes saves, to read back. */
export interface PastPreferencesMonth {
  period: string
  /** When its schedule was released, or null when its preferences closed without a release yet. */
  releasedAt: string | null
}

export interface ShiftPreferencesView {
  workerId: string
  facilityId: string
  period: string
  preferences: ShiftPreferences | null
  pto: PtoSummary
  offered: OfferedShiftType[]
  /** The caps for this worker's tier and employment type, from the facility's scheduling rules. */
  limits: PreferenceLimits
  /** This month and next by the calendar; which of them take saves is `accepting`. */
  periodsOpen: { current: string; next: string }
  /** The months a save is taken for: the earliest whose close is still ahead, and the one after it unless the rules take one. Absent from an API older than 17 October 2026. */
  accepting?: string[]
  /** Whether this view's month still takes saves. */
  acceptingSaves?: boolean
  /** When this view's month's schedule was released; null while it is not, absent from an older API. A released month takes no more preferences. */
  releasedAt?: string | null
  /** The worker's earlier months with saved preferences that no longer take saves, newest first; absent from an older API. */
  past?: PastPreferencesMonth[]
  /** When this view's month's bid window opens (or opened); null without one, absent from an older API. */
  opensAt?: string | null
  /** When this view's month's preferences close (or closed). */
  closesAt?: string
  /** The facility's zone, the one `closesAt` is midnight in. */
  timezone?: string
}

/** What a worker may ask for under the facility's scheduling rules (`FacilityScheduling.limits`, tier by employment type). */
export interface PreferenceLimits {
  tier: WorkerTier
  employmentType: EmploymentType
  /** False for a tier the facility assigns rather than asks (probationary, by default). */
  bids: boolean
  maxHighPriorityDays: number
  hoursMin: number
  hoursMax: number
  /** The tier's floor and ceiling for the type, the worker's own contracted hours folded in where the facility reads them. */
  typicalHours: { min: number; max: number }
  /** True where the floor is the ceiling: the Hours step is a statement. */
  fixedHours: boolean
  contractualFloor: 'flag' | 'block'
  /** The most PTO days the period allows this tier beside the balance; null when only the balance applies. */
  ptoDaysPerPeriod: number | null
  contractedHoursPerWeek: number | null
  /** True for a worker who marks the days they can work rather than days off; their floor is then flagged, never enforced. Absent from an API deployed before 22 October 2026. */
  picksAvailableDays?: boolean
}

/** Body of `PUT /workers/{workerId}/shift-preferences`. */
export interface SaveShiftPreferencesInput {
  period: string
  shiftTypes: ShiftTypePreference[]
  hours: { min: number; max: number }
  ptoDates: string[]
  daysOff: DayOffPreference[]
  /** Required of a worker who marks availability (`limits.picksAvailableDays`), left out by everyone else. */
  availableDates?: string[] | null
  note?: string | null
  /** The version last read; null for a first save. */
  version: number | null
}

export interface FacilityShiftPreferences {
  period: string
  periodsOpen: { current: string; next: string }
  preferences: ShiftPreferences[]
}

// The draft the engine builds from the preferences ("Build my schedule").

export type DraftFlag = 'under_target' | 'used_medium_day' | 'used_low_day' | 'nothing_published' | 'assigned' | 'medium_day_override' | 'pto_day_denied' | 'weekly_overtime' | 'daily_overtime' | 'day_off_taken'

/** A day off the facility-wide run took to fill a seat a minimum staffing line was short on: the day, the priority the worker gave it, the seat. */
export interface TakenDayOff {
  date: string
  priority: DayOffPriority
  shiftId: string
}

export type RuleSeverity = 'block' | 'review' | 'flag'

/** A rule's note kept on the draft: a review for the manager, a flag for the worker, or the block that leaves a draft under its floor. */
export interface DraftException {
  code: string
  severity: RuleSeverity
  /** The shift's date, or null for a note about the whole month. */
  date: string | null
  message: string
}

/** What became of each PTO day asked for: denied by the tier's rule, approved outright, or left for the run. */
export interface PtoDecision {
  date: string
  status: 'approved' | 'denied' | 'requested'
  reason: string | null
}

export interface DraftAssignment {
  shiftId: string
  date: string
  shiftType: ShiftTypeKey
  startTime: string
  endTime: string
  unitId: string
  hours: number
  /** The seat's role, recorded since 21 October 2026 (F5); absent on older drafts and runs. */
  role?: WorkerRole | null
}

/** The part of the month a draft was built for: from today for the month already running, else the whole month. */
export interface DraftWindow {
  from: string
  to: string
  days: number
  ofDays: number
  minHours: number
  floorHours: number
}

/** One worker's draft for a month: what they asked for, in shifts, claiming nothing; the scheduler resolves contests when it builds the month. */
export interface DraftSchedule {
  workerId: string
  facilityId: string
  period: string
  assignments: DraftAssignment[]
  hours: number
  flags: DraftFlag[]
  usedDaysOff: { date: string; priority: 'medium' | 'low' }[]
  /** The dates the tier's rule turned from a Medium day off into work. Absent from an API deployed before the scheduling rules. */
  overrides?: string[]
  /** Days off the run took for a short required seat; absent on a draft built alone or from an older API. */
  takenDaysOff?: TakenDayOff[]
  ptoDecisions?: PtoDecision[]
  exceptions?: DraftException[]
  /** A fingerprint of the facility's scheduling rules the draft was built under. */
  rulesVersion?: string
  /** A fingerprint of the month's published shifts the draft was built from; null or absent on an older draft. */
  publishedVersion?: string | null
  /** The days the draft could use - from today for the month already running - with the minimums scaled to them. Null or absent on an older draft. */
  window?: DraftWindow | null
  status: 'built' | 'accepted'
  builtAt: string
  /** 0 for an assigned draft built from no preferences. */
  preferencesVersion: number
  acceptedAt: string | null
  version: number
}

export interface DraftScheduleView {
  workerId: string
  facilityId: string
  period: string
  draft: DraftSchedule | null
  /** True when the preferences, the facility's rules or the month's published shifts changed after the draft was built. */
  isStale: boolean
  preferencesVersion: number | null
}

export interface FacilityDrafts {
  period: string
  drafts: DraftSchedule[]
}

// The facility-wide run (scheduling rules plan phase B): every worker drafted in rank order, contests settled.

export type ScheduleRunStatus = 'built' | 'released'
export type ContestBasis = 'tier' | 'score' | 'seniority' | 'order'
export type WorkerRunOutcome = 'scheduled' | 'assigned' | 'no_preferences' | 'nothing_fit'

export interface LostShift {
  shiftId: string
  date: string
  shiftType: ShiftTypeKey
  /** Who got it; null when the ranked build left it unfilled after all. */
  toWorkerId: string | null
}

/** One worker's part of a run: what they asked for, what they got, what went to someone ranked ahead. */
export interface WorkerRunResult {
  workerId: string
  name: string
  tier: WorkerTier
  score: number
  employmentType: EmploymentType
  hireDate: string | null
  outcome: WorkerRunOutcome
  asked: number
  awarded: DraftAssignment[]
  hours: number
  lost: LostShift[]
  /** Days off the run took from this worker for a seat a minimum line was short on, each with the seat's shift type; absent on older runs. */
  takenDaysOff?: (TakenDayOff & { shiftType: ShiftTypeKey })[]
  flags: DraftFlag[]
  exceptions: DraftException[]
  ptoDecisions: PtoDecision[]
  preferencesVersion: number
  /** A bidder who saved no preferences: drafted from last month's (`previous_month`) or assigned last from what was left (`none`). Absent otherwise, and on older runs. */
  preferencesSource?: 'previous_month' | 'none'
}

export interface ShiftContest {
  shiftId: string
  date: string
  shiftType: ShiftTypeKey
  candidates: { workerId: string; tier: WorkerTier; score: number; hireDate: string | null }[]
  awardedTo: string | null
  /** How many seats the shift has (phase D); absent from an API deployed before it. */
  capacity?: number
  /** Everyone who got a seat, in rank order (phase D). */
  awarded?: string[]
  basis: ContestBasis | null
}

export interface ScheduleRunTotals {
  workers: number
  scheduled: number
  assigned: number
  noPreferences: number
  nothingFit: number
  shiftsPublished: number
  shiftsFilled: number
  shiftsUnfilled: number
  contests: number
  /** Dates and lines below the facility's minimum staffing (phase D); absent from an older API. */
  belowMinimum?: number
  /** Days off the run took to fill short required seats; absent on older runs. */
  daysOffTaken?: number
}

export interface ScheduleRunSummary {
  runId: string
  facilityId: string
  period: string
  status: ScheduleRunStatus
  ranAt: string
  ranBy: RecordedBy
  rulesVersion: string
  beforeClose: boolean
  /** True when the engine built it because preferences closed; absent when staff pressed Build. */
  builtAtClose?: boolean
  totals: ScheduleRunTotals
  releasedAt: string | null
  releasedBy: RecordedBy | null
  /** Seats staff filled by hand before the release (month health plan §5); absent on a run with none. */
  assignments?: RunAssignment[]
  /** Set when the release went past a block of the month's health (month health plan §4): who said why, and the blocks in brief. */
  /** True when the engine released it at the end of the review window (facility operations dashboard plan §5). */
  releasedAutomatically?: boolean
  /** Staff's hold on the engine's release; null or absent when not held. */
  held?: { at: string; by: RecordedBy; note: string | null } | null
  releasedOver?: { reasonCode: string; note: string | null; findings: { key: HealthFindingKey; message: string; unitId: string | null; shiftType: AvailableShift['period'] | null; role: WorkerRole | null; count: number }[] }
  version: number
}

/** A seat staff filled by hand before the release (month health plan §5). */
export interface RunAssignment {
  shiftId: string
  workerId: string
  date: string
  shiftType: AvailableShift['period']
  unitId: string
  assignedAt: string
  assignedBy: RecordedBy
  reasonCode: string | null
  note: string | null
  overriddenRules: string[]
}

/** A month rule a placement would pass; a reason code is required when any. */
export type AssignRuleBroken = 'day_after_evening' | 'max_consecutive_days' | 'over_week_max' | 'weekly_overtime'

/** A worker who could take an unfilled seat, in rank order, with what the shift's week holds. */
export interface RunAssignCandidate {
  workerId: string
  name: string
  role: WorkerRole
  tier: WorkerTier
  score: number
  employmentType: EmploymentType
  hireDate: string | null
  weekHours: number
  weekHoursAfter: number
  shiftsThisWeek: number
  wouldBreak: AssignRuleBroken[]
}

export interface RunAssignCandidates {
  shift: AvailableShift
  candidates: RunAssignCandidate[]
}

export interface AssignSeatInput {
  shiftId: string
  workerId: string
  reasonCode?: string
  note?: string
}

/** Body of a release: nothing, or the override a block is released past. */
export interface ReleaseRunInput {
  override?: { reasonCode: string; note?: string }
}

export interface ScheduleRun extends ScheduleRunSummary {
  results: WorkerRunResult[]
  contests: ShiftContest[]
  unfilled: AvailableShift[]
  /** The month against the facility's minimum staffing as the run left it (phase D); absent from an older API. */
  coverage?: { linesApplied: number; gaps: CoverageRow[] }
}

/** One date against one minimum staffing line (phase D). */
export interface CoverageRow {
  date: string
  /** Null for every unit. */
  unitId: string | null
  shiftType: ShiftTypeKey
  /** Null for any role. */
  role: WorkerRole | null
  /** The line's floor on the date: its count, or its ratio at the day's census (F5). */
  minimum: number
  staffed: number
  short: number
  /** A ratio line's residents per `minimum`, and the census it was judged at; absent on a fixed line. */
  perResidents?: number
  census?: number
}

/** `GET /facilities/{facilityId}/schedule/coverage`: the month against every minimum line. */
export interface ScheduleCoverageView {
  facilityId: string
  period: string
  basis: 'released' | 'latest_run' | 'none'
  runId: string | null
  linesApplied: number
  rows: CoverageRow[]
  gaps: CoverageRow[]
  datesShort: string[]
}

export interface ScheduleRunList {
  period: string
  runs: ScheduleRunSummary[]
}

export type SchedulePeriodStatus = 'bidding' | 'closed' | 'built' | 'released'

/** Where a month stands at a facility: its timeline from the scheduling rules and what has happened so far. */
export interface SchedulePeriodView {
  facilityId: string
  period: string
  timezone: string
  /** When the month's bid window opens; null without one, absent from an API older than 19 October 2026. */
  preferencesOpenAt?: string | null
  preferencesCloseAt: string
  draftLocksAt: string
  releaseAt: string
  changeWindowClosesAt: string
  status: SchedulePeriodStatus
  /** Whether the engine builds the month when its preferences close; absent from an API older than 19 October 2026. */
  buildAtClose?: boolean
  /** Whether the month still takes workers' preferences; a save after its close is refused. */
  acceptingPreferences: boolean
  /** On the roster for the month; of them, the tiers that bid and the tiers the facility assigns. */
  rosterSize: number
  bidding: number
  assigned: number
  /** Workers who bid and saved preferences for the month. */
  preferencesSaved: number
  /** Drafts submitted, built but not submitted, and behind what they were built from. */
  draftsAccepted: number
  draftsBuilt: number
  draftsStale: number
  /** Published shifts in the month, one per seat, and the days with nothing published. */
  shiftsPublished: number
  daysWithoutShifts: number
  latestRun: ScheduleRunSummary | null
  releasedRun: ScheduleRunSummary | null
  /** When the engine releases the built run (plan §5): the close plus the review days, or the close; null where staff alone release. Absent from an API older than 25 October 2026. */
  engineReleasesAt?: string | null
  /** The earliest month the facility has a run or a published shift for, null for neither: how far back a month chooser steps (demo history plan H0). Absent from an older API. */
  firstPeriod?: string | null
}

/** Where one worker stands for the month before it is built: whether their tier bids, what they saved, their draft. */
export interface PeriodWorkerReadiness {
  workerId: string
  name: string
  role: WorkerRole
  tier: WorkerTier
  score: number
  employmentType: EmploymentType
  hireDate: string | null
  leaveDate: string | null
  bids: boolean
  preferences: { submittedAt: string; updatedAt: string; hours: { min: number; max: number }; ptoDays: number; daysOff: number } | null
  draft: { status: DraftSchedule['status']; builtAt: string; acceptedAt: string | null; isStale: boolean; shifts: number; hours: number } | null
  /** `save_preferences` when a reminder would help (they bid, saved none, the month takes them); `preferences_closed` for the same worker once the close has passed; null otherwise. */
  reminder: ReminderKind | null
  /** When staff last reminded them about the month, if ever. */
  remindedAt: string | null
}

/** What a reminder asks: the worker's preferences for the month, the one thing the build works from. */
export type ReminderKind = 'save_preferences' | 'preferences_closed'

/** What Remind all (`POST .../schedule/period/remind`) answers: worker ids sent a reminder, reminded within the day already, and failed. */
export interface RemindAllResult {
  period: string
  sent: string[]
  remindedRecently: string[]
  failed: string[]
}

/** What `POST .../schedule/period/workers/{workerId}/remind` answers: the row with the reminder just sent. */
export interface ScheduleReminderSent {
  period: string
  worker: PeriodWorkerReadiness
  reminder: { workerId: string; facilityId: string; period: string; kind: ReminderKind; sentAt: string; count: number }
}

/** What `GET /facilities/{facilityId}/schedule/period/workers` answers: the month's view and everyone on its roster, in rank order. Staff only. */
export interface PeriodReadinessView {
  period: string
  view: SchedulePeriodView
  workers: PeriodWorkerReadiness[]
}

/** The worker's part of the released month, or none yet. */
export interface WorkerScheduleResultView {
  workerId: string
  facilityId: string
  period: string
  releasedAt: string | null
  result: WorkerRunResult | null
}

// Clinical Bridge callouts (docs/plans/clinical-bridge-plan.md section 4.2).

/** The shift that needs covering. Clock times are in the facility's zone; an end at or before the start is the next morning. */
export interface BridgeCalloutShift {
  date: string
  start: string
  end: string
  unitId: string
  unitName?: string
  role: WorkerRole
}

export interface BridgeCalloutPay {
  rate: number
  currency: string
  premiumPerHour: number | null
}

export type BridgeCalloutStatus = 'open' | 'filled' | 'expired' | 'cancelled'
export type BridgeHeldBackReason = 'overtime' | 'by_administrator'

/** One worker the callout went to. `hoursThisWeek` is what the platform knows from reported outcomes; null when it knows nothing. */
export interface BridgePoolMember {
  workerId: string
  name: string
  tier: WorkerTier
  hoursThisWeek: number | null
  notifiedAt: string | null
}

export interface BridgeHeldBack {
  workerId: string
  name: string
  reason: BridgeHeldBackReason
  hoursThisWeek: number | null
}

export interface BridgeCalloutResponse {
  workerId: string
  answer: 'accept' | 'decline'
  at: string
}

export interface BridgeCallout {
  calloutId: string
  clientId: string
  facilityId: string
  shift: BridgeCalloutShift
  shiftStartsAt: string
  shiftEndsAt: string
  absentWorkerId: string | null
  reason: string | null
  pay: BridgeCalloutPay | null
  /** The board's open seat it was opened for; null, or absent from an older API, when opened on its own. */
  postingId?: string | null
  arrivalWindowMinutes: number
  responseWindowMinutes: number
  pool: BridgePoolMember[]
  heldBack: BridgeHeldBack[]
  /** What a reader sees now: an open callout past `expiresAt` reads as expired. */
  status: BridgeCalloutStatus
  responses: BridgeCalloutResponse[]
  filledBy: { workerId: string; acceptedAt: string; responseSeconds: number } | null
  /** Once filled, the acceptor's reported outcome for the shift's date, linked when the partner reports it; null until then. */
  outcome: BridgeCalloutOutcome | null
  openedAt: string
  openedBy: RecordedBy
  /** True when opened inside the facility's hard lock: the administrator's override. Absent on older records. */
  insideLock?: boolean
  expiresAt: string
  closedAt: string | null
  closedBy: RecordedBy | null
  version: number
}

/** The reported outcome of the shift a callout was filled for, as the partner reported it. */
export interface BridgeCalloutOutcome {
  kind: 'completed' | 'callout' | 'no_show'
  eventId: string
  recordedAt: string
}

export type BridgeAnswer = 'accept' | 'decline'

/** How a callout stands for one pool member. */
export type BridgeCalloutResult = 'open' | 'yours' | 'covered' | 'expired' | 'withdrawn'

/** A callout as the worker in its pool sees it (`GET /workers/{workerId}/bridge/callouts`): the shift, what accepting asks, their own answer and where they stand. */
export interface WorkerBridgeCallout {
  calloutId: string
  facilityId: string
  shift: BridgeCalloutShift
  shiftStartsAt: string
  shiftEndsAt: string
  pay: BridgeCalloutPay | null
  arrivalWindowMinutes: number
  responseWindowMinutes: number
  openedAt: string
  expiresAt: string
  status: BridgeCalloutStatus
  poolSize: number
  myAnswer: BridgeCalloutResponse | null
  result: BridgeCalloutResult
  filledAt: string | null
  outcome: BridgeCalloutOutcome | null
}

/** The pool a callout would go to, as `GET .../bridge/callouts/pool` answers it. */
export interface BridgePoolPreview {
  pool: BridgePoolMember[]
  heldBack: BridgeHeldBack[]
  shiftHours: number
}

/** Body of `POST /facilities/{facilityId}/bridge/callouts`. */
export interface OpenBridgeCalloutInput {
  shift: BridgeCalloutShift
  absentWorkerId?: string
  reason?: string
  pay?: { rate: number; currency?: string; premiumPerHour?: number | null }
  holdBack?: string[]
  /** The open seat on the board the callout is for: the board shows the callout on it and fills it with whoever takes the callout. */
  postingId?: string
}

// The schedule board (scheduling rules plan phase C): after the release - the change window, the trade board, the open board, the ledger.

export type LedgerEntry =
  | { kind: 'shift_type_changed'; at: string; workerId: string; fromShiftId: string; toShiftId: string; requestId: string }
  | { kind: 'swapped'; at: string; workerId: string; shiftId: string; withWorkerId: string; theirShiftId: string | null; via: 'change_window' | 'trade_board'; swapId: string }
  | { kind: 'swap_reversed'; at: string; swapId: string; by: string }
  | { kind: 'vacated'; at: string; workerId: string; shiftId: string; reason: string; by: RecordedBy }
  /** The census fell (census response plan F2): the shift was released from the worker, the seat held back. */
  | { kind: 'flexed_off'; at: string; workerId: string; shiftId: string; evaluationId: string; step: string; by: RecordedBy | null }
  /** The census fell and the worker moved to an open seat on another unit instead. */
  | { kind: 'floated'; at: string; workerId: string; fromShiftId: string; toShiftId: string; evaluationId: string; by: RecordedBy | null }
  /** A held seat went back to the worker it was flexed from. */
  | { kind: 'recalled'; at: string; workerId: string; shiftId: string; evaluationId: string; by: RecordedBy | null }
  /** A held seat's recall was declined or ran out: it is a posting on the open board now. */
  | { kind: 'released_to_board'; at: string; shiftId: string; evaluationId: string; postingId: string }
  /** A seat the plan requires beyond what is published was posted on the open board: by the census response (F3, naming its evaluation), or by staff from an uncovered gap (evaluationId null). */
  | { kind: 'census_seat_added'; at: string; postingId: string; evaluationId: string | null; by?: RecordedBy }
  | { kind: 'picked_up'; at: string; workerId: string; postingId: string; mode: PickupMode; by: RecordedBy | null; reasonCode: string | null; overriddenRules?: OverridableRule[] }

export type PickupMode = 'offer' | 'first_come' | 'override' | 'bridge'

/** The month rules a supervisor's override may pass with its reason code; role, unit, credentials and a second shift that day it never does. */
export type OverridableRule = 'day_after_evening' | 'max_consecutive_days'
export type ChangeKind = 'shift_type' | 'swap'
export type ChangeStatus = 'pending' | 'granted' | 'declined'

export interface ChangeRequest {
  requestId: string
  workerId: string
  submittedAt: string
  kind: ChangeKind
  shiftId: string
  toShiftType: ShiftTypeKey | null
  withWorkerId: string | null
  theirShiftId: string | null
  peerAnswer: 'accepted' | 'declined' | null
  peerAnsweredAt: string | null
  status: ChangeStatus
  decidedAt: string | null
  reason: string | null
}

export interface SubmitChangeInput {
  kind: ChangeKind
  shiftId: string
  toShiftType?: ShiftTypeKey
  withWorkerId?: string
  theirShiftId?: string
}

export type SwapStatus = 'offered' | 'needs_approval' | 'completed' | 'denied' | 'cancelled' | 'exhausted'

export interface BoardOffer {
  workerId: string
  offeredAt: string
  expiresAt: string
  answer: 'accepted' | 'declined' | null
  answeredAt: string | null
}

export interface SwapRequest {
  swapId: string
  requesterId: string
  shiftId: string
  theirShiftId: string | null
  requestedAt: string
  cascade: string[]
  offers: BoardOffer[]
  status: SwapStatus
  acceptedBy: string | null
  approvalReasons: string[]
  approval: { by: RecordedBy; at: string; decision: 'approved' | 'denied' } | null
  completedAt: string | null
  cancelledAt: string | null
  cancelledBy: string | null
}

export interface RequestSwapInput {
  shiftId: string
  withWorkerId: string
  theirShiftId?: string
}

export interface SwapCandidate {
  workerId: string
  name: string
  tier: string
  score: number
  theirShiftId: string | null
  pendingOffers: number
}

export type PostingSource = 'unfilled' | 'vacated' | 'bridge_callout' | 'census_rise' | 'demo'
export type PostingStatus = 'open' | 'filled' | 'closed'
/** `ended`: the shift is over and the seat went unfilled; absent from an API older than 25 October 2026, which kept it `locked`. */
export type PostingPhase = 'waiting' | 'offers' | 'first_come' | 'locked' | 'ended'
/** Where the clock has taken an open seat, the same in either fill mode (open shift lifecycle plan section 3). */
export type PostingStage = 'waiting' | 'posted' | 'urgent' | 'critical' | 'started' | 'ended'
/** An automatic move the engine has ahead of it for an open seat (plan section 8). */
export interface PostingMoveAhead {
  at: string
  stage: 'posted' | 'urgent' | 'bridge_callout' | 'critical' | 'started'
}
/** One step of a seat's life (plan section 8), as the API derives it. */
/** The lifecycle setting that fired a move of a seat's life, with its interval as an ISO 8601 duration. */
export interface PostingHistoryRule {
  setting: 'postedBefore' | 'nudgeEvery' | 'urgentBefore' | 'urgentRepeatEvery' | 'bridgeCalloutBefore' | 'lockBefore' | 'offerFor' | 'ranShortGrace'
  interval: string
}

export interface PostingHistoryEntry {
  kind: 'born' | 'posted' | 'nudged' | 'offered' | 'offer_declined' | 'offer_expired' | 'blasted' | 'repeated' | 'declined' | 'callout_opened' | 'locked' | 'filled' | 'ran_short' | 'withdrawn' | 'ended_unfilled'
  at: string
  /** Who made the move (seat life reasons plan). */
  by: 'engine' | 'worker' | 'staff' | 'partner' | 'demo'
  /** The engine's account of the move: the trigger, the rule and interval that fired, what it means. Composed on the API; printed as it comes. */
  why: string
  rule?: PostingHistoryRule
  told?: number
  workerId?: string
  until?: string
  calloutId?: string
  mode?: string
  reasonCode?: string
  reason?: string
  source?: string
  bornOf?: string
  vacatedFrom?: string | null
}

export interface PostingShift {
  shiftId: string | null
  date: string
  shiftType: ShiftTypeKey
  startTime: string
  endTime: string
  unitId: string
  role: WorkerRole | null
}

export interface Posting {
  postingId: string
  source: PostingSource
  /** Why the seat was born (open shift lifecycle plan section 2); absent on an older posting. */
  bornOf?: 'roster_short' | 'change_granted' | 'call_out' | 'call_out_reported' | 'call_out_by_worker' | 'census_rise' | 'bridge_unanswered' | 'left' | 'recall_expired' | 'demo'
  /** The urgent blast's repeats (plan section 3), each with who was told. */
  repeats?: { at: string; told: string[] }[]
  /** First come: when each worker was last told of this seat in their daily digest (plan section 3). */
  digested?: { workerId: string; at: string }[]
  /** When the seat entered its lock still open (plan section 6.2). */
  criticalAt?: string
  /** When the Clinical Bridge callout `calloutId` names was opened for this seat. */
  calloutLinkedAt?: string
  /** When the engine asked for a Clinical Bridge callout for this seat (plan D11). */
  engineCalloutAt?: string
  /** The seat was withdrawn as no longer needed (plan D13); the posting is then closed. */
  withdrawn?: { reason: 'census_fall' | 'unpublished' | 'demo'; at: string; by: RecordedBy | null }
  /** The shift started with nobody in the seat (plan D15); the posting is then closed. */
  endedUnfilled?: { at: string }
  /** The seat's own intervals as ISO durations (plan D24): a demo seat. */
  lifecycle?: Record<string, string | null>
  calloutId: string | null
  shift: PostingShift
  postedAt: string
  vacatedFrom: { workerId: string; reason: string } | null
  status: PostingStatus
  offers: BoardOffer[]
  blastAt: string | null
  filledBy: string | null
  filledAt: string | null
  pickupMode: PickupMode | null
  reasonCode: string | null
  /** The month rules a supervisor's override passed; absent when none. */
  overriddenRules?: OverridableRule[]
  /** For the reader's moment. */
  phase: PostingPhase
  stage: PostingStage
  history: PostingHistoryEntry[]
  ahead: PostingMoveAhead[]
  /** Who the blast told, at `blastAt`; absent on a posting blasted before 26 October 2026. */
  told?: string[]
  /** Who said no in first come, where no offer records it. */
  declined?: { workerId: string; at: string }[]
  /** Staff closed it as run short (facility operations dashboard plan D6). */
  ranShort?: { reasonCode: string; note: string | null; at: string }
}

/** Why someone of the shift's role was not told about an open seat. */
export type NotToldReason = 'left' | 'other_unit' | 'credentials' | 'working_that_day' | 'day_after_evening' | 'max_consecutive_days' | 'not_yet_offered' | 'free_since_blast'

/** One channel a notice went out on, as its newest delivery receipt says. */
export interface NoticeChannel {
  channel: string
  status: 'sent' | 'failed' | 'skipped' | 'address_gone' | 'opted_out' | 'held'
  reason: string | null
  at: string
  heldUntil: string | null
}

export interface NoticeReach {
  notificationId: string
  sentAt: string
  readAt: string | null
  read: boolean
  channels: NoticeChannel[]
}

export interface PostingAudienceMember {
  workerId: string
  name: string
  tier: WorkerTier
  score: number
  employmentType: EmploymentType
  weekHours: number
  via: 'offer' | 'first_come'
  toldAt: string
  expiresAt: string | null
  answer: 'accepted' | 'declined' | null
  answeredAt: string | null
  reach: NoticeReach | null
}

/** `GET /facilities/{facilityId}/schedule/board/postings/{postingId}/audience`: who was told about an open seat, and who was not and why. */
export interface PostingAudience {
  postingId: string
  phase: PostingPhase
  stage: PostingStage
  history: PostingHistoryEntry[]
  ahead: PostingMoveAhead[]
  shift: PostingShift
  status: PostingStatus
  blastAt: string | null
  filledBy: { workerId: string; name: string; at: string; mode: PickupMode | null } | null
  told: PostingAudienceMember[]
  notTold: { reason: NotToldReason; workers: { workerId: string; name: string }[] }[]
}

export interface ScheduleBoardView {
  facilityId: string
  period: string
  runId: string
  openedAt: string
  changeRequests: ChangeRequest[]
  windowClosedAt: string | null
  swaps: SwapRequest[]
  postings: Posting[]
  ledger: LedgerEntry[]
  /** Seats the census took back (census response plan F2); absent from a board from before. */
  held?: HeldSeat[]
  /** Who volunteered for a low-census day, by date. */
  volunteers?: FlexVolunteer[]
  version: number
  changeWindowClosesAt: string
  schedule: { workerId: string; name: string; assignments: DraftAssignment[]; hours: number }[]
}

export type HeldSeatStatus = 'held' | 'recall_offered' | 'recalled' | 'released'

/** A seat the census took from a worker: held, not posted, with their first right to it while the recall runs. */
export interface HeldSeat {
  shiftId: string
  date: string
  shiftType: ShiftTypeKey
  startTime: string
  endTime: string
  unitId: string
  role: WorkerRole | null
  requiredCredentials?: string[]
  workerId: string
  flexedAt: string
  recallUntil: string
  evaluationId: string
  status: HeldSeatStatus
  recallOfferedAt: string | null
  answeredAt: string | null
}

export interface FlexVolunteer {
  workerId: string
  date: string
  at: string
}

/** How a shift the worker holds came to them after the release (worker calendar design, 26 October 2026). */
export interface DayOrigin {
  shiftId: string
  date: string
  kind: 'picked_up' | 'assigned' | 'swapped_in' | 'moved' | 'recalled'
  at: string
}

/** A shift the worker held after the release and no longer does. */
export interface DayRemoval {
  shiftId: string
  date: string
  shiftType: ShiftTypeKey
  startTime: string
  endTime: string
  unitId: string
  kind: 'vacated' | 'swapped_away' | 'flexed_off'
  at: string
  /** The facility's reason, for a vacated shift. */
  reason: string | null
}

export interface WorkerBoardView {
  workerId: string
  period: string
  changeWindowClosesAt: string
  changeWindowOpen: boolean
  assignments: DraftAssignment[]
  hours: number
  changes: LedgerEntry[]
  myChangeRequests: ChangeRequest[]
  incomingChangeRequests: ChangeRequest[]
  mySwaps: SwapRequest[]
  incomingSwaps: SwapRequest[]
  postings: Posting[]
  /** Seats the census took from this worker, held with their recall deadline (F2). Absent from an API older than 20 October 2026. */
  flexed?: HeldSeat[]
  /** Held seats offered back to this worker, awaiting their answer. */
  recalls?: HeldSeat[]
  /** The dates this worker volunteered for a low-census day off. */
  volunteerDates?: string[]
  /** How each of the worker's shifts came to them since the release. Absent from an API older than 26 October 2026. */
  origins?: DayOrigin[]
  /** The worker's shifts taken off since the release. */
  removed?: DayRemoval[]
  /** The worker's home unit; null when they have none. */
  homeUnitId?: string | null
  /** The facility's units by id, named as the facility calls them. */
  unitNames?: Record<string, string>
}

export interface VacateShiftInput {
  workerId: string
  shiftId: string
  reason: string
}

export interface AssignPostingInput {
  workerId: string
  reasonCode: string
}

// --- The census (census response plan F0): a count per unit per day, never a resident.

export type CensusKind = 'actual' | 'expected'
export type CensusSource = 'staff' | 'partner' | 'import'
export type CensusBasis = 'recorded' | 'weekday_average' | 'planned'

export interface CensusEntry {
  facilityId: string
  unitId: string
  date: string
  census: number
  kind: CensusKind
  source: CensusSource
  recordedAt: string
  recordedBy: RecordedBy
}

/** Body of `PUT /facilities/{facilityId}/census`; `kind` left out is actual up to today and expected after. */
export interface CensusEntryInput {
  unitId: string
  date: string
  census: number
  kind?: CensusKind
}

export interface CensusUnitView {
  id: string
  name: string
  beds: number | null
  plannedCensus: number | null
}

/** The census a day is planned on: recorded, else the same weekday's average, else the unit's planned census; capped at the beds. */
export interface CensusFigure {
  census: number
  kind: CensusKind | 'forecast'
  basis: CensusBasis
  source: CensusSource | null
  recordedAt: string | null
  sample?: number
  overBeds: boolean
}

/** One date on one unit; `census` null where there is no count and nothing to forecast from. */
export type CensusRow = { date: string; unitId: string } & (CensusFigure | { census: null })

export interface CensusView {
  facilityId: string
  from: string
  to: string
  forecastWeeks: number
  units: CensusUnitView[]
  rows: CensusRow[]
  /** Patient days over the range per unit, recorded and forecast apart. */
  ppd: { unitId: string; recorded: number; forecast: number }[]
}

export interface RecordCensusResult {
  written: number
  entries: CensusEntry[]
  /** The census response (F2): one record per date and unit of a released month just recorded. Absent from an API older than 20 October 2026. */
  evaluations?: CensusEvaluationRecord[]
}

export type StaffingFlag = 'no_staffing_plan' | 'census_above_grid' | 'census_below_grid' | 'split_not_100'

export interface RoleRequirement {
  role: WorkerRole
  methodHours: number
  /** Whole seats of the shift's length. */
  seats: number
  /** Short shifts beside the whole seats under `short_shift` rounding (F5): 0 or 1. Absent from an API older than 21 October 2026. */
  shortSeats?: number
  /** The length of a short shift where one is planned; 0 otherwise. */
  shortHours?: number
  hours: number
  trace: string[]
}

/** The short shift a fraction of a seat became on a shift type (F5). */
export interface ShortShiftWindow {
  hours: number
  start: string
  end: string
}

export interface ShiftRequirement {
  shiftType: ShiftTypeKey
  /** The shift's productive hours: its span less its unpaid break. */
  shiftHours: number
  roles: RoleRequirement[]
  seats: number
  hours: number
  /** The short shift's window under `short_shift` rounding; null under the other roundings. Absent from an API older than 21 October 2026. */
  short?: ShortShiftWindow | null
}

export interface HppdFigures {
  total: number | null
  byRole: Partial<Record<WorkerRole, number>>
}

/** What a day's census requires on a unit: PPD × target HPPD × the shift's split as hours, rounded up into seats, then fixed positions and the minimum lines as floors. */
export interface StaffingRequirement {
  unitId: string
  census: number
  method: 'hppd' | 'grid' | null
  shifts: ShiftRequirement[]
  seats: number
  hours: number
  hppd: { target: HppdFigures | null; planned: HppdFigures | null }
  flags: StaffingFlag[]
}

export type CensusRequirementFigure = (CensusFigure & { given?: false }) | { census: number; given: true }

/** `GET /facilities/{facilityId}/census/requirements/range`: several days' requirements in one read, each as the single-day read answers it. */
export interface CensusRequirementsRangeView {
  facilityId: string
  from: string
  to: string
  days: CensusRequirementsView[]
}

export interface CensusRequirementsView {
  facilityId: string
  date: string
  units: { unit: CensusUnitView; figure: CensusRequirementFigure | null; requirement: StaffingRequirement | null }[]
}

// --- The census evaluation (census response plan F1): what a census would change on a released month.

export type CensusVerdict = 'no_change' | 'rise' | 'fall' | 'nothing_to_judge'
export type CensusBlocker = 'not_released' | 'no_staffing_plan' | 'inside_lock' | 'inside_notice'
export type FlexStepKey = 'volunteers' | 'agency' | 'prn' | 'tier' | 'rotation' | 'seniority' | 'overtime' | 'part_time'

export interface CensusEvaluationLine {
  shiftType: ShiftTypeKey
  role: WorkerRole
  /** True for the short shift's line under `short_shift` rounding (F5); absent on a record from before 21 October 2026. */
  short?: boolean
  shiftHours: number
  required: number
  staffed: number
  delta: 'same' | 'add' | 'release'
  count: number
  floorHeld: boolean
  blocker: 'inside_lock' | 'inside_notice' | null
}

export interface CensusFlexedWorker {
  workerId: string
  name: string
  tier: WorkerTier
  score: number
  employmentType: EmploymentType
  hireDate: string | null
}

export interface ProposedRelease {
  shiftType: ShiftTypeKey
  role: WorkerRole
  shiftId: string
  worker: CensusFlexedWorker
  step: FlexStepKey
  because: string
  action: 'float' | 'flex'
  floatTo: { shiftId: string; unitId: string } | null
  hours: number
}

export interface ProposedAddition {
  shiftType: ShiftTypeKey
  role: WorkerRole
  seats: number
  hours: number
  /** The short shift's window where the seats are short shifts (F5); null for whole seats, absent on an older record. */
  short?: ShortShiftWindow | null
  offeredFirst: CensusFlexedWorker[]
}

export interface CensusEvaluation {
  facilityId: string
  period: string
  date: string
  unitId: string
  census: number
  verdict: CensusVerdict
  mode: 'automatic' | 'propose'
  requirement: StaffingRequirement | null
  hppd: { target: number | null; scheduled: number | null; after: number | null }
  lines: CensusEvaluationLine[]
  releases: ProposedRelease[]
  additions: ProposedAddition[]
  holds: CensusEvaluationLine[]
  blockers: CensusBlocker[]
  evaluatedAt: string
}

export interface CensusEvaluationView {
  facilityId: string
  date: string
  unitId: string
  figure: CensusRequirementFigure | null
  evaluation: CensusEvaluation | null
  reason: 'census_missing' | null
}

export type CensusEvaluationStatus = 'proposed' | 'applied' | 'dismissed' | 'noted'

/** A census evaluation as stored (census response plan F2): the evaluation made when a census was recorded and what became of it. */
export interface CensusEvaluationRecord {
  evaluationId: string
  facilityId: string
  period: string
  date: string
  unitId: string
  status: CensusEvaluationStatus
  evaluation: CensusEvaluation
  censusBasis: CensusBasis | 'given'
  createdAt: string
  createdBy: RecordedBy
  heldBecause: 'mode_propose' | 'inside_notice' | 'inside_lock' | 'rise_not_applied_yet' | null
  decidedAt: string | null
  decidedBy: RecordedBy | null
  applied: { flexed: number; floated: number; recalled?: number; posted?: number } | null
}

// --- The staffing report (census response plan §3.6): PPD and HPPD by unit and day.

export interface StaffingDay {
  date: string
  unitId: string
  census: number | null
  scheduledHours: number
  workedHours: number
  agencyHours: number
  flexedHours: number
  scheduledHppd: number | null
  workedHppd: number | null
  /** Saturday or Sunday (F6). Absent from an API older than 21 October 2026. */
  weekend?: boolean
  /** The day's hours by role, with the worked HPPD (F6). Absent from an older API. */
  byRole?: { role: WorkerRole; scheduledHours: number; workedHours: number; workedHppd: number | null }[]
}

/** The days of a span with a census, weekend or weekday, summed (F6). */
export interface StaffingDayKindSummary {
  ppd: number
  days: number
  scheduledHours: number
  workedHours: number
  scheduledHppd: number | null
  workedHppd: number | null
}

export interface StaffingRoleHours {
  role: WorkerRole
  scheduledHours: number
  workedHours: number
  scheduledHppd: number | null
  workedHppd: number | null
  targetHppd: number | null
}

export interface StaffingUnitMonth {
  period: string
  unitId: string
  unitName: string
  ppd: number
  daysWithCensus: number
  daysInMonth: number
  scheduledHours: number
  workedHours: number
  agencyHours: number
  flexedHours: number
  scheduledHppd: number | null
  workedHppd: number | null
  targetHppd: number | null
  byRole: StaffingRoleHours[]
  /** The days with a census, weekend and weekday apart (F6). Absent from an older API. */
  weekend?: StaffingDayKindSummary
  weekday?: StaffingDayKindSummary
  days: StaffingDay[]
}

export interface StaffingReport {
  facilityId: string
  from: string
  to: string
  targetHppd: number | null
  units: StaffingUnitMonth[]
  totals: { ppd: number; scheduledHours: number; workedHours: number; agencyHours: number; flexedHours: number; scheduledHppd: number | null; workedHppd: number | null; weekend?: StaffingDayKindSummary; weekday?: StaffingDayKindSummary }
}

// --- The workforce plan (census response plan F6, the SNF guide's section 7): FTEs, hires and the master pattern.

export interface WorkforcePatternLine {
  line: string
  group: 'A' | 'B'
  /** The two weeks, Monday first. */
  days: boolean[]
}

export interface WorkforcePattern {
  seatsPerDay: number
  shiftHours: number
  shiftsPerWeek: number
  fullTimeLines: number
  groups: { A: number; B: number }
  weekendOnlyLines: number
  lines: WorkforcePatternLine[]
  coverage: number[]
}

export interface WorkforceGroup {
  unitId: string
  shiftType: ShiftTypeKey
  role: WorkerRole
  seatsPerDay: number
  shortSeatsPerDay: number
  shortHours: number
  shiftHours: number
  pattern: WorkforcePattern
  holders: { workerId: string; name: string }[]
  linesOpen: number
}

export interface WorkforceUnit {
  unitId: string
  unitName: string
  census: number | null
  basis: 'planned' | 'recent_average' | 'none'
  sample?: number
  requirement: StaffingRequirement | null
  groups: WorkforceGroup[]
}

export interface RoleWorkforce {
  role: WorkerRole
  dailyHours: number
  targetDailyHours: number
  weeklyHours: number
  fteNeeded: number
  fteWithBuffer: number
  fteOnRoster: number
  headcount: Record<EmploymentType, number>
  fteGap: number
  hires: number
}

export type WorkforceFlag = 'no_staffing_plan' | 'unit_without_census' | 'posted_late'

/** `GET /facilities/{facilityId}/reports/workforce`: what the budgeted census asks of the roster. Staff. */
export interface WorkforcePlan {
  facilityId: string
  asOf: string
  hoursPerFte: number
  bufferPct: number
  postedAheadDays: number
  units: WorkforceUnit[]
  roles: RoleWorkforce[]
  totals: { dailyHours: number; targetDailyHours: number; fteNeeded: number; fteWithBuffer: number; fteOnRoster: number; fteGap: number; hires: number }
  unplaced: { workerId: string; name: string; role: WorkerRole; reason: 'no_usual_shift' | 'no_unit' }[]
  flags: WorkforceFlag[]
}

// --- The month's health (month health plan phase 0, 21 October 2026).

export type HealthSeverity = 'block' | 'warn' | 'info'
export type HealthLens = 'census' | 'workers' | 'units'
export type HealthVerdict = 'ready' | 'warnings' | 'blocked'
/** What remedies a finding, for the Month card's button. */
export type HealthAction = 'record_census' | 'configure_units' | 'set_staffing_plan' | 'publish_shifts' | 'add_worker' | 'edit_worker' | 'remind_all' | 'build_month' | 'assign_seats' | 'open_board' | 'release'
export type HealthFindingKey =
  | 'no_staffing_plan'
  | 'unit_without_capacity'
  | 'census_missing'
  | 'census_expected_only'
  | 'unit_without_census'
  | 'below_requirement'
  | 'requirement_not_published'
  | 'below_minimum_line'
  | 'minimum_not_published'
  | 'over_published'
  | 'unfilled_over_cap'
  | 'fill_below_minimum'
  | 'role_short_on_roster'
  | 'no_prn_or_agency'
  | 'preferences_missing'
  | 'preferences_closed_missing'
  | 'credentials_lapsing'
  | 'not_built'
  | 'run_stale'
  | 'not_released'
  | 'no_units'
  | 'nothing_published'
  | 'unit_without_shifts'
  | 'shifts_on_unknown_unit'
  | 'days_without_shifts'

/** One thing wrong with the month: where, how much, and what remedies it. */
export interface HealthFinding {
  key: HealthFindingKey
  lens: HealthLens
  severity: HealthSeverity
  unitId: string | null
  shiftType: AvailableShift['period'] | null
  role: WorkerRole | null
  dates: string[]
  count: number
  workerIds: string[]
  message: string
  action: HealthAction | null
  /** For a seat never published: how many are missing on each date; absent from other findings and from an API older than 22 October 2026. */
  seatsByDate?: Record<string, number>
}

export interface UnitHealth {
  unitId: string
  name: string
  daysWithCensus: number
  /** Of the days with a count, the actual and the expected ones; absent from an API before 26 October 2026. */
  daysActual?: number
  daysExpected?: number
  /** The last day with an actual count; null when none has one. */
  actualThrough?: string | null
  daysForecast: number
  daysWithoutCensus: number
  shiftsPublished: number
  seatsRequired: number
  seatsHeld: number
  seatsShort: number
  seatsSurplus: number
  /** The same seats per shift type; absent from an API older than 25 October 2026. */
  byShift?: { shiftType: ShiftTypeKey; required: number; held: number; published: number }[]
}

export interface MonthHealthCounts {
  shiftsPublished: number
  unfilled: number
  seatsRequired: number
  seatsHeld: number
  seatsShort: number
  seatsSurplus: number
  belowMinimum: number
  preferencesMissing: number
  /** Workers on the roster whose tier bids; absent from an API older than 22 October 2026. */
  bidding?: number
}

/** What `GET /facilities/{facilityId}/schedule/health` answers: the findings in three lenses, the units' numbers, the verdict the release guard reads. */
export interface MonthHealthView {
  facilityId: string
  period: string
  timezone: string
  status: SchedulePeriodStatus
  basis: 'released' | 'latest_run' | 'none'
  runId: string | null
  verdict: HealthVerdict
  findings: HealthFinding[]
  byUnit: UnitHealth[]
  counts: MonthHealthCounts
  /** How much of the month the roster fills before the release, against the plan's required seats (else the published); null until a run exists, and absent from an API older than 25 October 2026. */
  fill?: MonthFill | null
  /** The distinct actions the warnings and blocks name, in the order to take them. */
  actions: HealthAction[]
  evaluatedAt: string
}

// --- What needs a person (facility operations dashboard plan §4): `GET /facilities/{facilityId}/schedule/decisions`.

export type DecisionKind = 'locked_gap' | 'ended_unfilled' | 'uncovered_gap' | 'proposed_flex' | 'proposed_rise' | 'swap_approval' | 'release_blocked' | 'census_not_recorded'
export type DecisionSeverity = 'urgent' | 'approval' | 'proposed' | 'reminder'
export type DecisionActionKind = 'post_gap' | 'assign' | 'open_callout' | 'run_short' | 'apply' | 'dismiss' | 'approve' | 'deny' | 'assign_seats' | 'release_over' | 'hold' | 'record_census'

export interface DecisionAction {
  kind: DecisionActionKind
  method: 'GET' | 'POST' | 'PUT'
  path: string
  who?: 'staff' | 'operator' | 'facility_administrator' | 'nobody'
}

export interface DecisionCandidate {
  workerId: string
  name: string
  tier: WorkerTier
  score: number
  employmentType: EmploymentType
  weekHours: number
  wouldBreak: string[]
}

export interface Decision {
  id: string
  kind: DecisionKind
  severity: DecisionSeverity
  at: string
  date: string | null
  unitId: string | null
  shiftType: ShiftTypeKey | null
  role: WorkerRole | null
  title: string
  reasoning: string[]
  candidates?: DecisionCandidate[]
  /** A locked gap: how many seats of the shift are open and their postings, soonest first - one decision per short shift. Absent from an API older than 25 October 2026 (one seat each). */
  seats?: number
  postingIds?: string[]
  /** An uncovered gap: the slot as the coverage grid reads it, so the two say the same numbers (26 October 2026). */
  coverage?: { required: number; staffed: number; posted: number; locked: number; ranShort: number; uncovered: number; census: number | null; censusRecorded: boolean; startTime: string; endTime: string }
  actions: DecisionAction[]
  source: { kind: 'posting' | 'evaluation' | 'swap' | 'run' | 'census' | 'coverage'; id: string; period: string | null }
}

export interface DecisionsView {
  facilityId: string
  timezone: string
  evaluatedAt: string
  counts: { total: number; urgent: number; approval: number; proposed: number; reminder: number }
  decisions: Decision[]
}

/** Body of `POST /facilities/{facilityId}/schedule/board/gaps`: an uncovered gap's seats to post (at most those uncovered). */
export interface PostGapInput {
  date: string
  unitId: string
  shiftType: ShiftTypeKey
  role: WorkerRole
  seats?: number
}

/** Body of `POST .../board/postings/{postingId}/run-short` (plan D6). */
export interface RunShortInput {
  reasonCode: string
  note?: string
}

/** The month's fill (facility operations dashboard plan §6): required seats, held, left open for PRN, agency and the board, and the share the facility requires filled. */
export interface MonthFill {
  basis: 'plan' | 'published'
  required: number
  filled: number
  open: number
  openPct: number
  /** Absent from an API older than the minimum-filled rule; read through `filledPctOf`. */
  filledPct?: number
  minFilledPct?: number | null
}

/** The filled share of a month's fill, from an API that names it or one that names only the open share. */
export function filledPctOf(fill: MonthFill): number {
  return fill.filledPct ?? Math.round((100 - fill.openPct) * 10) / 10
}

// --- The facility heatmap (26 October 2026): `GET /facilities/{facilityId}/schedule/heatmap` and `.../heatmap/history`.

/** One scale for a slot, a day and a month, best to worst; `none` where nothing is required. */
export type HeatState = 'covered' | 'near' | 'thin' | 'short' | 'critical' | 'none'

/** The share of required seats held at or above which a day reads `near`, `thin` and `short`; the API's, so a filtered view is judged the same. */
export interface HeatScale {
  near: number
  thin: number
  short: number
}

export type HeatDayPhase = 'worked' | 'today' | 'ahead'

export interface HeatTally {
  required: number
  staffed: number
  onBoard: number
  ranShort: number
  uncovered: number
}

export interface HeatmapSlot {
  unitId: string
  shiftType: ShiftTypeKey
  role: WorkerRole
  startTime: string
  endTime: string
  phase: PostingPhase
  stage: PostingStage
  history: PostingHistoryEntry[]
  ahead: PostingMoveAhead[]
  required: number
  staffed: number
  posted: number
  locked: number
  ranShort: number
  uncovered: number
  census: number | null
  censusRecorded: boolean
  state: HeatState
}

export interface HeatmapDay extends HeatTally {
  date: string
  phase: HeatDayPhase
  state: HeatState
  slots: HeatmapSlot[]
}

export interface FacilityHeatmapView {
  facilityId: string
  period: string
  timezone: string
  today: string
  released: boolean
  hasStaffingPlan: boolean
  scale: HeatScale
  units: { id: string; name: string }[]
  state: HeatState
  totals: { worked: HeatTally; ahead: HeatTally; month: HeatTally }
  byState: Record<HeatState, number>
  days: HeatmapDay[]
}

export interface HeatmapHistoryMonth {
  period: string
  released: boolean
  state: HeatState
  totals: HeatTally
  days: { date: string; phase: HeatDayPhase; state: HeatState; required: number; staffed: number }[]
}

export interface FacilityHeatmapHistoryView {
  facilityId: string
  today: string
  scale: HeatScale
  months: HeatmapHistoryMonth[]
}
